import type { GetAllRequestParameters } from '@pct-ng/core/dto';
import { AbstractDtoService } from '@pct-ng/core/dto';
import {
  type ProgramGetDTO,
  type ProgramListDTO,
  type ProgramPostDTO,
  type ProgramPutDTO,
  ProgramService,
} from '@szebit-app/sdk/backend';
import { Injectable } from '@angular/core';
import type { HttpContext } from '@angular/common/http';
import type { ListPaginatedDTO } from '@pct-sdk/core';
import { map, type Observable } from 'rxjs';
import { isNil } from '@pct-ts/typeguard';

@Injectable()
export class ProgramDtoService extends AbstractDtoService<
  ProgramService,
  ProgramPostDTO,
  ProgramGetDTO,
  ProgramListDTO,
  'id',
  ProgramPutDTO
> {
  constructor() {
    super({ sort: 'name', direction: 'ASC' }, ProgramService);
  }

  override create(dto: ProgramPostDTO, httpContext?: HttpContext): Observable<ProgramGetDTO> {
    return this.service
      .createProgram(dto, httpContext)
      .pipe(map(() => dto as unknown as ProgramGetDTO));
  }

  override update(
    id: string,
    dto: ProgramPutDTO,
    httpContext?: HttpContext,
  ): Observable<ProgramGetDTO> {
    return this.service.updateProgram(id, dto, httpContext);
  }

  override delete(id: string, httpContext?: HttpContext): Observable<void> {
    return this.service.deleteProgram(id, httpContext);
  }

  override getById(id: string, httpContext?: HttpContext): Observable<ProgramGetDTO> {
    return this.service.findProgram(id, httpContext);
  }

  override getAll(
    { pagination, sort }: GetAllRequestParameters<any>,
    httpContext?: HttpContext,
  ): Observable<ListPaginatedDTO<ProgramListDTO, any>> {
    return this.service.listPrograms(
      sort?.field,
      // eslint-disable-next-line sonarjs/no-nested-conditional
      sort?.order === undefined ? undefined : sort?.order === 'asc' ? 'Asc' : 'Desc',
      pagination?.size,
      pagination?.page,
      httpContext,
    );
  }

  override getDtoId(dto: ProgramGetDTO): string;
  override getDtoId(dto: ProgramGetDTO | null | undefined): string | null | undefined;
  override getDtoId(dto: Partial<ProgramGetDTO> | null | undefined): string | null | undefined;
  override getDtoId(
    dto: ProgramGetDTO | Partial<ProgramGetDTO> | null | undefined,
  ): string | null | undefined {
    if (isNil(dto)) {
      return dto;
    }
    return dto.id;
  }

  override toString(dto: ProgramGetDTO): string {
    return dto.name ?? 'UNKNOWN ITEM';
  }

  listPublicPrograms(): Observable<ProgramListDTO[]> {
    return this.service.listPublicPrograms();
  }
}
